import React, { useState } from 'react';

const QueryExamples = ({ onExampleClick }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const queryExamples = [
    {
      category: "Basic Selections",
      examples: [
        {
          name: "Simple S-PLUS Select with Limit",
          description: "Select top 100 objects from S-PLUS DR4",
          query: `SELECT TOP 100 *
FROM splus.splus_dr4
WHERE r_auto < 20`
        },
        {
          name: "Select Specific S-PLUS Columns",
          description: "Select coordinates and photometry from S-PLUS",
          query: `SELECT id, ra, dec, r_auto, g_auto, i_auto
FROM splus.splus_dr4
WHERE r_auto < 18 AND r_auto > 0
ORDER BY r_auto ASC`
        },
        {
          name: "S-PLUS Narrow-band Filters",
          description: "Select objects with narrow-band photometry",
          query: `SELECT id, ra, dec, j0660_auto, j0861_auto, r_auto
FROM splus.splus_dr4
WHERE j0660_auto > 0 AND j0861_auto > 0
  AND r_auto < 19`
        },
        {
          name: "Simple DELVE Select",
          description: "Select objects from DELVE DR2 with photometric redshifts",
          query: `SELECT TOP 100 *
FROM public.delve_dr2
WHERE z_phot_peak > 0 AND z_phot_peak < 2`
        },
        {
          name: "DELVE Photometry Selection",
          description: "Select DELVE objects with good photometry",
          query: `SELECT id, ra, dec, mag_auto_g, mag_auto_r, z_phot_peak
FROM public.delve_dr2
WHERE mag_auto_g < 23 AND mag_auto_r < 23
  AND z_phot_peak BETWEEN 0.1 AND 1.5
ORDER BY mag_auto_r ASC`
        }
      ]
    },
    {
      category: "Cone Searches",
      examples: [
        {
          name: "S-PLUS Cone Search",
          description: "Search for S-PLUS objects within a circular region",
          query: `SELECT TOP 1000 id, ra, dec, r_auto, g_auto
FROM splus.splus_dr4
WHERE 1=CONTAINS(POINT(ra, dec), CIRCLE(150.0, 2.0, 0.1))
  AND r_auto < 20`
        },
        {
          name: "DELVE Cone Search with Distance",
          description: "DELVE cone search with angular distance calculation",
          query: `SELECT TOP 500 id, ra, dec, mag_auto_g, z_phot_peak,
       DISTANCE(POINT(ra, dec), POINT(150.0, 2.0)) AS angular_distance
FROM public.delve_dr2
WHERE 1=CONTAINS(POINT(ra, dec), CIRCLE(150.0, 2.0, 0.05))
ORDER BY angular_distance ASC`
        },
        {
          name: "S-PLUS Cone Search with Color Selection",
          description: "S-PLUS cone search with color constraints",
          query: `SELECT id, ra, dec, r_auto, g_auto, (g_auto - r_auto) AS g_r_color
FROM splus.splus_dr4
WHERE 1=CONTAINS(POINT(ra, dec), CIRCLE(180.0, -30.0, 0.2))
  AND r_auto < 19
  AND (g_auto - r_auto) BETWEEN 0.2 AND 1.5`
        }
      ]
    },
    {
      category: "Cross-Match Between Catalogs",
      examples: [
        {
          name: "S-PLUS and DELVE Cross-Match",
          description: "Positional cross-match between S-PLUS DR4 and DELVE DR2",
          query: `SELECT s.id AS splus_id, s.ra AS splus_ra, s.dec AS splus_dec,
       s.r_auto AS splus_r, d.id AS delve_id, d.mag_auto_r AS delve_r,
       d.z_phot_peak,
       DISTANCE(POINT(s.ra, s.dec), POINT(d.ra, d.dec)) AS separation
FROM splus.splus_dr4 AS s
JOIN public.delve_dr2 AS d 
  ON 1=CONTAINS(POINT(s.ra, s.dec), CIRCLE(d.ra, d.dec, 0.001))
WHERE s.r_auto < 21 AND d.mag_auto_r < 23
ORDER BY separation ASC`
        },
        {
          name: "Color-Color Analysis",
          description: "Cross-match for color-color analysis",
          query: `SELECT s.id, s.ra, s.dec,
       s.g_auto AS splus_g, s.r_auto AS splus_r, s.i_auto AS splus_i,
       d.mag_auto_g AS delve_g, d.mag_auto_r AS delve_r,
       d.z_phot_peak, d.z_phot_err
FROM splus.splus_dr4 AS s
JOIN public.delve_dr2 AS d 
  ON 1=CONTAINS(POINT(s.ra, s.dec), CIRCLE(d.ra, d.dec, 0.0008))
WHERE s.r_auto BETWEEN 16 AND 22
  AND d.z_phot_peak BETWEEN 0.1 AND 1.0`
        }
      ]
    },
    {
      category: "Advanced Selections",
      examples: [
        {
          name: "S-PLUS Star-Galaxy Separation",
          description: "Select stars vs galaxies using S-PLUS morphology",
          query: `SELECT id, ra, dec, r_auto, class_star,
       fwhm_n, elongation, ellipticity
FROM splus.splus_dr4
WHERE r_auto < 20
  AND class_star > 0.8  -- likely stars
  AND fwhm_n > 0`
        },
        {
          name: "DELVE High-z Galaxy Candidates",
          description: "Select high-redshift galaxy candidates from DELVE",
          query: `SELECT id, ra, dec, mag_auto_g, mag_auto_r,
       z_phot_peak, z_phot_err, z_phot_odds
FROM public.delve_dr2
WHERE z_phot_peak > 1.5
  AND z_phot_err < 0.3
  AND z_phot_odds > 0.7
  AND mag_auto_r < 24`
        },
        {
          name: "S-PLUS Emission Line Candidates",
          description: "Select potential emission line objects using narrow-band excess",
          query: `SELECT id, ra, dec, r_auto, j0660_auto, j0861_auto,
       (j0660_auto - r_auto) AS halpha_excess,
       (j0861_auto - i_auto) AS ca_triplet_excess
FROM splus.splus_dr4
WHERE r_auto < 20
  AND j0660_auto > 0 AND j0861_auto > 0
  AND (j0660_auto - r_auto) > 0.1  -- H-alpha excess
ORDER BY (j0660_auto - r_auto) DESC`
        }
      ]
    },
    {
      category: "Table Upload & Match",
      examples: [
        {
          name: "Upload Table with S-PLUS Match",
          description: "Join uploaded targets with S-PLUS DR4",
          query: `SELECT u.name, u.ra AS input_ra, u.dec AS input_dec,
       s.id AS splus_id, s.r_auto, s.g_auto, s.i_auto,
       DISTANCE(POINT(u.ra, u.dec), POINT(s.ra, s.dec)) AS separation
FROM upload.my_targets AS u
JOIN splus.splus_dr4 AS s
  ON 1=CONTAINS(POINT(u.ra, u.dec), CIRCLE(s.ra, s.dec, 0.002))
WHERE s.r_auto < 22
ORDER BY u.name, separation ASC`
        },
        {
          name: "Nearest DELVE Match for Uploads",
          description: "Find nearest DELVE match for each uploaded source",
          query: `SELECT u.object_id, u.ra AS input_ra, u.dec AS input_dec,
       d.id AS delve_id, d.ra AS catalog_ra, d.dec AS catalog_dec,
       d.mag_auto_g, d.mag_auto_r, d.z_phot_peak,
       DISTANCE(POINT(u.ra, u.dec), POINT(d.ra, d.dec)) AS separation
FROM upload.sources AS u
CROSS JOIN (
  SELECT id, ra, dec, mag_auto_g, mag_auto_r, z_phot_peak,
         ROW_NUMBER() OVER (ORDER BY DISTANCE(POINT(u.ra, u.dec), POINT(ra, dec))) AS rn
  FROM public.delve_dr2
  WHERE 1=CONTAINS(POINT(ra, dec), CIRCLE(u.ra, u.dec, 0.01))
) AS d
WHERE d.rn = 1`
        }
      ]
    }
  ];

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  return (
    <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-sm border border-ui-light-border dark:border-ui-dark-border">
      <div className="p-3 border-b border-ui-light-border dark:border-ui-dark-border">
        <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Query Examples
        </h3>
        <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-1">
          Click any example to load it into the editor
        </p>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {queryExamples.map((category, categoryIndex) => (
          <div key={categoryIndex} className="border-b border-ui-light-border dark:border-ui-dark-border last:border-b-0">
            <button
              onClick={() => toggleSection(categoryIndex)}
              className="w-full px-3 py-2 text-left hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  {category.category}
                </span>
                <svg
                  className={`w-4 h-4 text-ui-light-text-secondary dark:text-ui-dark-text-secondary transform transition-transform ${
                    expandedSection === categoryIndex ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            {expandedSection === categoryIndex && (
              <div className="pb-2">
                {category.examples.map((example, exampleIndex) => (
                  <div key={exampleIndex} className="mx-3 mb-2">
                    <button
                      onClick={() => onExampleClick(example.query)}
                      className="w-full text-left p-2 rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors border border-ui-light-border dark:border-ui-dark-border"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-primary-600 dark:text-primary-400">
                            {example.name}
                          </h4>
                          <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-1">
                            {example.description}
                          </p>
                        </div>
                        <svg
                          className="w-4 h-4 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary ml-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      
                      <div className="mt-2 p-2 bg-ui-light-hover dark:bg-ui-dark-hover rounded text-xs font-mono text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                        {example.query.split('\n').slice(0, 2).join('\n')}
                        {example.query.split('\n').length > 2 && '...'}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QueryExamples;